import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import { Button, Flex, SimpleGrid, Text, useBreakpointValue } from '@chakra-ui/react';

import { Modal } from '~/components/modal';
import { trackEvent } from '~/features/analytics/track-event';
import { Pagination } from '~/features/experiments/ab_plp_redesign_2025/components/pagination';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { ICON_SIZE, StarIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { logger } from '~/features/logging/logger';
import { getProgramReviews } from '~/features/program/controllers/get-program-reviews';
import type { ProgramReviews } from '~/features/program/models/program-review';
import { useClientRender } from '~/hooks/use-client-render';

import { CARD_BORDER_COLOR, MODAL_REVIEW_PAGE_SIZE } from '../constants';
import { formatCountLabel } from '../utils/format-count-label';
import { getReviewsTotalPages } from '../utils/get-reviews-total-pages';

import { ReviewCard, ReviewSkeletonCard } from './review-card';

export type ReviewsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  programKey: string;
  numberOfReviews: number;
  starsAverage: number;
};

/**
 * The Figma modal is 863px wide and lays its 12 reviews out four across. `5xl` runs past that so
 * the four columns get room to breathe, and the grid sizes the cards to the column so the row never
 * drops to three.
 */
const MODAL_SIZE = '5xl';
const MODAL_COLUMNS = { base: 1, sm: 2, lg: 4 };

/**
 * Figma `body/md` semibold. The theme's `md` is 15px, a size short of the design. Chakra's 16px
 * block padding crowds the title against the top edge, and a full-bleed sheet makes that edge the
 * top of the screen, so phones want the wider band at least as much as desktop does. The body
 * scrolls under the header, so the band below the title is all that separates it from a clipped
 * card; the rule gives that edge a reason to be there.
 */
const MODAL_HEADER_PROPS = {
  borderBottom: '1px solid',
  borderColor: CARD_BORDER_COLOR,
  fontSize: '16px',
  fontWeight: '600',
  lineHeight: '24px',
  // The UDS theme sets the header's padding with a shorthand, which zeroes the bottom and outranks
  // any `paddingBottom` passed here. Only another shorthand replaces it.
  padding: 6,
};

/**
 * Full bleed on phones. Chakra's sized dialog keeps a 4rem block margin, rounded corners and a
 * `calc(100% - 7.5rem)` cap, which spends the scarcest axis on a screen where a single column of
 * twelve cards already overflows. From `md` up the block margin is trimmed to 3rem, which buys the
 * three rows of cards enough height to land without an inner scroll on a laptop.
 */
const MODAL_CONTENT_PROPS = {
  borderRadius: { base: 0, md: 'md' },
  height: { base: '100dvh', md: 'auto' },
  marginY: { base: 0, md: 12 },
  maxHeight: { base: '100dvh', md: 'calc(100% - 6rem)' },
  maxWidth: { base: '100vw', md: MODAL_SIZE },
};

/**
 * The footer is the bottom edge of a full-bleed sheet, so phones want more room under the pager and
 * narrower side gutters to give the page numbers the width they need.
 */
const MODAL_FOOTER_PROPS = { paddingBottom: { base: 6, md: 4 }, paddingX: { base: 4, md: 6 } };

/**
 * Chakra's default `top: 2` leaves the 32px button riding above the centerline of the 24px title,
 * which reads as floating over the header rather than sitting in the corner. 20px centers the two
 * against the header's block padding, and `insetEnd` moves in to match.
 */
const MODAL_CLOSE_BUTTON_PROPS = { insetEnd: '16px', top: '20px' };

/** Eleven controls don't fit a phone; at 3 the widest arrangement is seven. */
const PAGINATION_MAX_VISIBLE_PAGES = { base: 3, md: 5 };
const PAGINATION_SPACING = { base: '4px', md: '8px' };

export function ReviewsModal({ isOpen, onClose, programKey, numberOfReviews, starsAverage }: ReviewsModalProps) {
  const [page, setPage] = useState(1);
  const totalPages = getReviewsTotalPages(numberOfReviews, MODAL_REVIEW_PAGE_SIZE);
  const isClientRender = useClientRender();
  const bodyRef = useRef<HTMLDivElement>(null);
  // The dialog only mounts on open, so there is no server render for the breakpoint to disagree with.
  const maxVisiblePages = useBreakpointValue(PAGINATION_MAX_VISIBLE_PAGES, { fallback: 'md' });
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';

  const {
    data: reviews,
    isLoading: reviewsIsLoading,
    isError: reviewsIsError,
    refetch,
  } = useQuery<ProgramReviews>(
    ['reviews-modal', programKey, page],
    () => getProgramReviews(programKey, page, MODAL_REVIEW_PAGE_SIZE),
    {
      enabled: isClientRender && isOpen,
      onError: (error) => {
        logger.error('Failed to load a page of PLP reviews for the reviews modal.', {
          file: 'src/features/program/project-first-plp/components/reviews-modal.tsx',
          function: 'ReviewsModal',
          programKey,
          page,
          error,
        });
      },
    }
  );

  // Reopening the modal should start over rather than resume a page the reader
  // has already left behind.
  useEffect(() => {
    if (!isOpen) setPage(1);
  }, [isOpen]);

  // The body keeps its scroll offset across a page change, which drops the reader into the middle
  // of a list they haven't seen the top of yet.
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [page]);

  function handlePageChange(nextPage: number) {
    // The standard reviews carousel fires this same label behind the `dino` flag.
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Product Page CTA Clicked',
        type: 'click',
        label: 'reviews_scroll_clicked',
        additionalAttributes: {
          product_ids: [programKey],
        },
      });
    }
    setPage(nextPage);
  }

  const hasReviews = Boolean(reviews?.length);

  // Pinned to the footer rather than trailing the grid: row heights vary with review length, so an
  // in-flow pager jumps to a new position on every page change.
  const pager = totalPages > 0 && (
    <Pagination
      currentPage={page}
      maxVisiblePages={maxVisiblePages}
      spacing={PAGINATION_SPACING}
      totalPages={totalPages}
      onChange={handlePageChange}
    />
  );

  return (
    <Modal
      footerButtons={pager}
      isOpen={isOpen}
      modalBodyRef={bodyRef}
      modalCloseButtonProps={MODAL_CLOSE_BUTTON_PROPS}
      modalContentProps={MODAL_CONTENT_PROPS}
      modalFooterProps={MODAL_FOOTER_PROPS}
      modalHeaderProps={MODAL_HEADER_PROPS}
      scrollBehavior='inside'
      size={MODAL_SIZE}
      title='Trustpilot reviews'
      onClose={onClose}
    >
      <Flex flexDirection='column' gap={4}>
        <Flex alignItems='center' gap='6px'>
          <StarIcon color='primary.orange' height={ICON_SIZE} width={ICON_SIZE} />
          <Text as='b' color='supportOrange.70' fontSize='md' fontWeight='600' lineHeight='md'>
            Average Rating: {starsAverage}{' '}
            <Text as='span' color='supportGray.100' fontSize='md' fontWeight='400' lineHeight='md'>
              ({formatCountLabel(numberOfReviews, 'Review')})
            </Text>
          </Text>
        </Flex>

        <SimpleGrid columns={MODAL_COLUMNS} spacing={4}>
          {reviewsIsLoading &&
            Array.from({ length: MODAL_REVIEW_PAGE_SIZE }).map((_, idx) => <ReviewSkeletonCard key={idx} />)}

          {!reviewsIsLoading &&
            reviews?.map((review, idx) => <ReviewCard key={review.createdAt + idx} review={review} />)}
        </SimpleGrid>

        {!reviewsIsLoading && reviewsIsError && (
          <Flex alignItems='center' flexDirection='column' gap={3} paddingY={8} textAlign='center'>
            <Text color='supportGray.100' fontSize='md' fontWeight='600'>
              We couldn&apos;t load these reviews
            </Text>
            <Text color='supportGray.70' fontSize='sm'>
              Something went wrong on our end. Please try again.
            </Text>
            <Button size='sm' variant='outline' onClick={() => refetch()}>
              Try again
            </Button>
          </Flex>
        )}

        {!reviewsIsLoading && !reviewsIsError && !hasReviews && (
          <Flex alignItems='center' flexDirection='column' gap={3} paddingY={8} textAlign='center'>
            <Text color='supportGray.100' fontSize='md' fontWeight='600'>
              No reviews on this page
            </Text>
            <Text color='supportGray.70' fontSize='sm'>
              Try an earlier page to keep reading.
            </Text>
          </Flex>
        )}
      </Flex>
    </Modal>
  );
}
